// KOMMENT-REAKCIÓK (emoji) — a reakció-sáv egy komment alatt + a „ki reagált" felugró lista.
//
// MIÉRT FIX EMOJI-KÉSZLET, ÉS NEM TELJES EMOJI-VÁLASZTÓ CSOMAG: a reakció szerepe a RÖVID visszajelzés
// („láttam" / „egyetértek" / „köszönöm"), és ezt a GitHub · Linear · Slack · Jira mind egy szűk, FIX
// készlettel oldja meg. Egy `emoji-mart`/`emoji-picker-react` dependency ~1 MB-os adatbázist és egy külön
// keresőt hozna a lapra azért, hogy az emberek 99%-ban ugyanazt az öt-hat jelet válasszák — miközben a
// szűk készlet i18n-, a11y- és szerződés-oldalon is kezelhető (a szerver KÖTÖTT enumot validál:
// `TICKET_REACTION_EMOJIS`). Ha egyszer kell a teljes paletta, a `REACTION_LABELS` és a szerződés-enum
// bővítése az egyetlen munka — a komponens változatlan marad.
//
// A NÉVSORREND NYELV-FÜGGŐ (`formatName`): magyarul vezetéknév elöl, más nyelven keresztnév elöl. A szerver
// ezért a NÉV-RÉSZEKET adja (`givenName`/`familyName`), nem egy összefűzött nevet.
import { TICKET_REACTION_EMOJIS } from '@atlas/contracts';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PersonChip } from '@/components/shared/person-chip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface ReactionActor {
  identityId?: string | undefined;
  isMe: boolean;
  name: string | null;
  givenName: string | null;
  familyName: string | null;
  avatarUrl?: string | null | undefined;
  /** A kolléga csapat-színe — az avatar háttere (partnernél null). */
  teamColor?: string | null | undefined;
}

export interface CommentReaction {
  emoji: string;
  count: number;
  reactedByMe: boolean;
  actors: ReactionActor[];
}

/**
 * A FELAJÁNLOTT készlet — a szerződés `TICKET_REACTION_EMOJIS`-ának TÜKRE (a szerver ezt validálja; a
 * kettő egyezését fal őrzi: `ticket-reaction-emojis.wall.spec.ts`). A SORREND szándékos: elöl a
 * visszajelzés (👍 a leggyakoribb), utána az érzelem, végül a munka-jelzés — a választó három hatos
 * sorban rendezi őket.
 */
export const REACTION_CHOICES = TICKET_REACTION_EMOJIS;

export interface TicketCommentReactionsProps {
  /**
   * A komment reakciói. SZÁNDÉKOSAN tolerálja a `undefined`-ot: a mező a szerződésben kötelező, de egy
   * RÉGEBBI BFF-válasz (vagy egy cache-elt régi lista) nem hordozza — és egy hiányzó tömb ilyenkor NEM
   * fehér lapot érdemel. Mérve élő dev-stacken: a friss web-bundle a még nem újraindult
   * BFF-fel „Cannot read properties of undefined (reading 'length')"-tel vitte el a jegy-részletet.
   */
  reactions?: readonly CommentReaction[] | undefined;
  /** Reakció be/ki (toggle) — a hívó a mutációt intézi (optimista frissítéssel). */
  onToggle: (emoji: string) => void;
  /** Folyamatban lévő toggle → a gombok tiltottak (a kettős klikk nem futhat kétszer). */
  pending?: boolean;
  /** Van-e joga reagálni (a lap `tickets.view`-ja) — jog nélkül CSAK olvasható a sáv. */
  canReact: boolean;
}

export function TicketCommentReactions({
  reactions,
  onToggle,
  pending = false,
  canReact,
}: TicketCommentReactionsProps): JSX.Element | null {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const list = reactions ?? [];

  // Se reakció, se jog → a sáv MEG SEM JELENIK (nulla zaj a szálon).
  if (list.length === 0 && !canReact) return null;

  // A SÁV egy EGYSZERŰ, inline sor: a POZICIONÁLÁS a hívóé (a komment-kártyán a szegélyre ülő
  // akció-sorban áll, a szerkesztés-gombbal együtt — user-döntés).
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {list.map((r) => (
        // A CHIP EGY vezérlő: KATTINTÁS = toggle, HOVER/FÓKUSZ = „ki reagált" (avataros lista a tooltipben).
        //
        // MIÉRT NEM popover a névlista: a chip kattintása a toggle, tehát a popovert csak egy MÁSODIK
        // vezérlővel (vagy a toggle elrontásával) lehetne nyitni — és billentyűzeten egyáltalán nem lenne
        // elérhető. A Radix tooltip HOVERRE ÉS FÓKUSZRA is nyílik, tehát a lista így klaviatúráról is
        // olvasható; a tartalma pedig lehet avataros lista (nem csak szöveg) — ez a GitHub/Linear minta.
        <Tooltip key={r.emoji}>
          <TooltipTrigger asChild>
            <button
              type="button"
              // A SAJÁT reakció kiemelt (brand-tónus + brand szegély): a `reactedByMe` a szerver igazsága,
              // nem kliens-emlékezet.
              className={cn(
                'ease-atlas bg-card flex items-center gap-1 rounded-full border px-2 py-1 text-[12px] shadow-sm transition-colors duration-150',
                r.reactedByMe
                  ? 'border-brand bg-brand-soft text-brand-dark dark:text-brand'
                  : 'border-border bg-card text-muted-foreground hover:border-brand hover:text-foreground',
                pending && 'pointer-events-none opacity-60',
              )}
              aria-pressed={r.reactedByMe}
              aria-label={t('tickets.comments.reactions.toggleLabel', {
                emoji: r.emoji,
                count: r.count,
              })}
              onClick={() => {
                if (canReact) onToggle(r.emoji);
              }}
              disabled={!canReact}
            >
              <span aria-hidden="true">{r.emoji}</span>
              <span className="tabular-nums">{r.count}</span>
            </button>
          </TooltipTrigger>
          {/* A „ki reagált" a TOOLTIP saját megjelenésével (sötét alap, fehér szöveg) — nem külön világos
              panel. NINCS fejléc-sor („Kik adták: 👍"): az emoji ott van a chipen, amire ráhúztunk, tehát a
              felirat ugyanazt mondta harmadszor (user-döntés, 2026-09-09). */}
          <TooltipContent side="top" className="max-w-64 px-2.5 py-2">
            <ul className="space-y-1">
              {/* A kulcs a névből + a sorszámból: a belső azonosító a PARTNER-válaszban szándékosan nincs. */}
              {r.actors.map((a, i) => (
                <li key={`${a.name ?? ''}-${String(i)}`}>
                  {/* a közös személy-chip: a monogram is a nyelv szerint formázott névből jön (HU „RS",
                      nem „SR" — user-lelet), ugyanúgy, mint a rendszer többi részén */}
                  <PersonChip
                    name={a.name}
                    givenName={a.givenName}
                    familyName={a.familyName}
                    avatarUrl={a.avatarUrl ?? null}
                    accentColor={a.teamColor ?? null}
                    size="sm"
                    className="gap-1.5 text-[12.5px]"
                  />
                </li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      ))}

      {canReact && (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="border-border bg-card text-muted-foreground hover:border-brand hover:text-foreground ease-atlas flex items-center rounded-full border p-1.5 shadow-sm transition-colors duration-150"
                  aria-label={t('tickets.comments.reactions.addLabel')}
                  disabled={pending}
                >
                  <FaceSmileIcon className="size-3.5" />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">{t('tickets.comments.reactions.addLabel')}</TooltipContent>
          </Tooltip>
          <PopoverContent align="start" className="w-auto p-1.5">
            {/* NYOLCAS RÁCS: a 30 jel négy sorban (a készlet a SZERZŐDÉSBŐL jön, tehát a rács magától nő,
                ha a lista bővül — nem kell itt számot tartani). Nincs kereső: 30 jel egyetlen pillantással
                átfogható, és a keresés emoji-adatbázist (≈1 MB) hozna a bundle-be. */}
            <div className="grid grid-cols-8 gap-1">
              {REACTION_CHOICES.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="hover:bg-accent ease-atlas flex size-8 items-center justify-center rounded-md text-[16px] transition-colors duration-150"
                  aria-label={t('tickets.comments.reactions.pickLabel', { emoji })}
                  onClick={() => {
                    setPickerOpen(false);
                    onToggle(emoji);
                  }}
                >
                  <span aria-hidden="true">{emoji}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
